import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from '@mui/material';
import to from 'await-to-js';
import { Alert, Button, DialogV2, FileUploadDropzone } from 'c-components';
import apiClient from 'c-data/apiClient';
import { useAPIClientRequest } from 'c-hooks';
import type { AllowedVenueUploadResponse } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import {
    allowedVenueSummaryRows,
    canSaveAllowedVenues,
    shownRejections,
} from './allowedVenueSummary';

type Props = {
    campaignId: number;
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
};

/**
 * UL-7912 -- upload the campaign's allowed venue list.
 *
 * Validate, then write. Choosing a file runs a dry run and shows the summary; nothing is saved until
 * Save is pressed, which sends the same file again for real. A malformed file never half-imports.
 *
 * The summary is the point, not the success. No venue list means nothing is ever unbooked, so the
 * danger is not a missing file but an incomplete one: rows read against rows derived is what shows a
 * plan that lost a network on the way, before the CAD does.
 */
const AllowedVenueUploadDialog: React.FC<Props> = ({ campaignId, open, onClose, onSaved }) => {
    const t = useCommonTranslation();
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<AllowedVenueUploadResponse | null>(null);

    const dryRun = useAPIClientRequest(apiClient.Entities.Campaign.uploadAllowedVenues);
    const save = useAPIClientRequest(apiClient.Entities.Campaign.uploadAllowedVenues);

    const { reset: resetDryRun, start: startDryRun } = dryRun;
    const { reset: resetSave, start: startSave } = save;

    useEffect(() => {
        if (!open) return;
        setFile(null);
        setPreview(null);
        resetDryRun();
        resetSave();
    }, [open, resetDryRun, resetSave]);

    const onFilesAdded = useCallback(
        async (files: File[]) => {
            const [chosen] = files;
            if (!chosen) return;

            setFile(chosen);
            setPreview(null);
            resetSave();

            const [err, res] = await to(startDryRun(campaignId, chosen, true));
            if (!err && res?.data) setPreview(res.data);
        },
        [campaignId, resetSave, startDryRun],
    );

    const onSave = useCallback(async () => {
        if (!file) return;

        const [err] = await to(startSave(campaignId, file, false));
        if (err) return;

        onSaved();
        onClose();
    }, [campaignId, file, onClose, onSaved, startSave]);

    const summary = preview?.summary;
    const canSave = canSaveAllowedVenues(preview);

    const rows = allowedVenueSummaryRows(preview).map(row => ({
        label: t(row.labelKey, row.labelParams),
        value: row.value,
    }));

    return (
        <DialogV2
            open={open}
            onClose={onClose}
            maxWidth="md"
            title="Modules.Main.Campaigns.CampaignSchedule.dsp.venues.title"
        >
            <Stack gap={2}>
                <FileUploadDropzone
                    fileAccept=".csv,.txt"
                    onFilesAdded={onFilesAdded}
                    disabled={dryRun.isLoading || save.isLoading}
                    dropzoneLabel="Modules.Main.Campaigns.CampaignSchedule.dsp.venues.dropzone"
                />

                {file && (
                    <Typography variant="body2">
                        {t('Modules.Main.Campaigns.CampaignSchedule.dsp.venues.file')}: {file.name}
                    </Typography>
                )}

                {dryRun.isLoading && (
                    <Stack direction="row" gap={1} alignItems="center">
                        <CircularProgress size={16} />
                        <Typography variant="body2">
                            {t('Modules.Main.Campaigns.CampaignSchedule.dsp.venues.validating')}
                        </Typography>
                    </Stack>
                )}

                {/* A file rejected whole -- no usable columns, or no usable rows -- says why. */}
                {dryRun.error && <Alert severity="error">{String(dryRun.error)}</Alert>}

                {summary && (
                    <Box>
                        <Table size="small">
                            <TableBody>
                                {rows.map(row => (
                                    <TableRow key={row.label}>
                                        <TableCell>{row.label}</TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{ fontVariantNumeric: 'tabular-nums' }}
                                        >
                                            {row.value}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {summary.rejections.length > 0 && (
                            <Stack mt={1}>
                                {shownRejections(summary).map(rejection => (
                                    <Typography
                                        key={rejection.line}
                                        variant="caption"
                                        color="error"
                                    >
                                        {t(
                                            'Modules.Main.Campaigns.CampaignSchedule.dsp.venues.rejectionLine',
                                            {
                                                line: rejection.line,
                                                reason: rejection.reason,
                                            },
                                        )}
                                    </Typography>
                                ))}
                            </Stack>
                        )}

                        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                            {t('Modules.Main.Campaigns.CampaignSchedule.dsp.venues.nothingWritten')}
                        </Typography>
                    </Box>
                )}

                {save.error && <Alert severity="error">{String(save.error)}</Alert>}

                <Stack direction="row" justifyContent="flex-end">
                    <Button onClick={onSave} disabled={!canSave || save.isLoading}>
                        {save.isLoading ? (
                            <CircularProgress size={20} />
                        ) : (
                            t('Modules.Main.Campaigns.CampaignSchedule.dsp.venues.save')
                        )}
                    </Button>
                </Stack>
            </Stack>
        </DialogV2>
    );
};

export default AllowedVenueUploadDialog;
